import { Obstacle, Templates, LogicalSpace, Group } from '@thive/fddm-types';

import { logger } from '@/modules/common/logging/logger';
import { templatesToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import {
  hasOnlySingleWallObstacle,
  hasOnlySingleObstacle,
  hasOnlySingleHighwayArea,
  hasOnlySingleSingularStationArea,
  hasOnlyManualArea,
  hasOnlySingleStationaryArea,
  hasOnlySingleRackingArea,
  hasOnlySingleLassieTemplate,
  hasOnlySingleShuttleRackTemplate,
  hasOnlySingleLassieMiniTemplate,
  hasOnlySingleGroup,
  hasOnlySingleHikTemplate,
} from '@/modules/floorplanDesign/helpers/selection';
import { Area, PanelDisplayMode, Panel_Display_Mode } from '@/modules/floorplanDesign/types';

/**
 * Update the url path natively. Avoids a react route component unmount/remount when route params change.
 * Use carefully.
 */
export const replaceURLPath = (path: string) => {
  window.history.replaceState(null, '', path);
};

export const determinePanelMode = (
  selectedObstacles: Obstacle[],
  selectedAreas: Area[],
  selectedTemplates: Templates,
  selectedGroups: Group[],
  selectedLogicalSpaces: LogicalSpace[],
): PanelDisplayMode => {
  const selectedTemplatesFlat = templatesToFlatCollection(selectedTemplates);

  if (
    !selectedAreas.length &&
    !selectedTemplatesFlat.length &&
    !selectedObstacles.length &&
    !selectedLogicalSpaces.length &&
    !selectedGroups.length
  ) {
    return Panel_Display_Mode.NONE;
  }
  if (
    (selectedAreas.length && selectedObstacles.length) ||
    (selectedAreas.length && selectedGroups.length)
  ) {
    return Panel_Display_Mode.MIX;
  }
  if (
    !selectedAreas.length &&
    !selectedTemplatesFlat.length &&
    !selectedLogicalSpaces.length &&
    !selectedGroups.length
  ) {
    if (hasOnlySingleWallObstacle(selectedObstacles)) {
      return Panel_Display_Mode.WALL;
    }
    if (hasOnlySingleObstacle(selectedObstacles)) {
      return Panel_Display_Mode.OBSTACLE;
    }
    return Panel_Display_Mode.OBSTACLES;
  }
  if (hasOnlySingleHighwayArea(selectedAreas)) {
    return Panel_Display_Mode.HIGHWAY;
  }
  if (hasOnlySingleSingularStationArea(selectedAreas)) {
    return Panel_Display_Mode.SINGULAR_STATION;
  }
  if (hasOnlyManualArea(selectedAreas)) {
    return Panel_Display_Mode.MANUAL;
  }
  if (hasOnlySingleStationaryArea(selectedAreas)) {
    return Panel_Display_Mode.STATIONARY;
  }
  if (hasOnlySingleRackingArea(selectedAreas)) {
    return Panel_Display_Mode.RACKING;
  }
  if (selectedAreas.length && !selectedTemplatesFlat.length && selectedGroups.length === 0) {
    return Panel_Display_Mode.AREA;
  }
  if (hasOnlySingleLassieTemplate(selectedTemplates)) {
    return Panel_Display_Mode.TEMPLATE_LASSIE;
  }
  if (hasOnlySingleShuttleRackTemplate(selectedTemplates)) {
    return Panel_Display_Mode.TEMPLATE_SHUTTLE_RACK;
  }
  if (hasOnlySingleLassieMiniTemplate(selectedTemplates)) {
    return Panel_Display_Mode.TEMPLATE_LASSIE_MINI;
  }
  if (hasOnlySingleHikTemplate(selectedTemplates)) {
    return Panel_Display_Mode.TEMPLATE_HIK;
  }
  if (selectedLogicalSpaces.length > 1) {
    return Panel_Display_Mode.LOGICAL_SPACES;
  }
  if (selectedLogicalSpaces.length === 1) {
    return Panel_Display_Mode.LOGICAL_SPACE;
  }
  if (!selectedAreas.length && !selectedGroups.length && selectedTemplatesFlat.length) {
    return Panel_Display_Mode.TEMPLATE;
  }
  if (hasOnlySingleGroup(selectedGroups)) {
    return Panel_Display_Mode.GROUP;
  }
  if (!selectedAreas.length && selectedGroups.length) {
    return Panel_Display_Mode.GROUPS;
  }

  logger.error('Unhandled panel mode for selected elements', selectedAreas, selectedTemplates);
  return Panel_Display_Mode.NONE;
};
